import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { dppApi, countdownApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Flame, Trophy, Target, Calendar,
  Loader2, CheckCircle2, XCircle, Sparkles, Zap, Award,
  TrendingUp, Clock, ChevronRight, Play, Star,
} from 'lucide-react';

// Daily Practice Problems page — subject-cards + streak + countdown + history grid
export default function DailyPractice() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('jee_main');
  const [today, setToday] = useState(null);
  const [streak, setStreak] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Active subject play state
  const [activeSubject, setActiveSubject] = useState(null); // { dpp_id, subject, questions }
  const [answers, setAnswers] = useState({});
  const [qIdx, setQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, c, h] = await Promise.all([
        dppApi.today(examId),
        dppApi.streak(examId),
        countdownApi.forExam(examId),
        dppApi.history(examId, 30),
      ]);
      setToday(t);
      setStreak(s);
      setCountdown(c);
      setHistory(h);
    } catch {
      toast.error('Failed to load Daily Practice');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh if any subject is still generating
  useEffect(() => {
    if (!today?.subjects) return;
    const pending = today.subjects.some(s => !s.ready);
    if (!pending) return;
    const id = setInterval(async () => {
      try {
        const t = await dppApi.today(examId);
        setToday(t);
        if (!t.subjects.some(s => !s.ready)) clearInterval(id);
      } catch { /* keep trying */ }
    }, 6000);
    return () => clearInterval(id);
  }, [today, examId]);

  const startSubject = async (subj) => {
    // If the subject is still generating, fetch it on-demand (blocks until ready)
    if (!subj.ready) {
      const loadingToast = toast.loading(`Generating ${subj.subject} DPP…`);
      try {
        const fresh = await dppApi.getSubject(examId, subj.subject);
        toast.dismiss(loadingToast);
        subj = fresh;
        await load();
      } catch {
        toast.dismiss(loadingToast);
        toast.error('Failed to generate DPP');
        return;
      }
    }
    setActiveSubject(subj);
    setAnswers({});
    setQIdx(0);
    setResult(null);
  };

  const submit = async () => {
    if (!activeSubject) return;
    setSubmitting(true);
    try {
      const r = await dppApi.submit(activeSubject.dpp_id, answers);
      setResult(r);
      if (r.new_badges?.length) {
        toast.success(`New badge${r.new_badges.length > 1 ? 's' : ''} earned!`);
      }
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  // ACTIVE PLAY VIEW
  if (activeSubject && !result) {
    const q = activeSubject.questions[qIdx];
    const isLast = qIdx === activeSubject.questions.length - 1;
    const answeredCount = Object.keys(answers).length;
    return (
      <div className="min-h-screen bg-background text-foreground" data-testid="dpp-play">
        <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => { if (window.confirm('Exit without submitting?')) setActiveSubject(null); }} data-testid="dpp-exit">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <p className="text-sm font-bold">{activeSubject.subject} · Daily Practice</p>
                <p className="text-xs text-muted-foreground">{answeredCount}/{activeSubject.questions.length} answered</p>
              </div>
            </div>
            <Badge variant="outline" className="font-mono">Q{qIdx + 1} / {activeSubject.questions.length}</Badge>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="dpp-question-panel">
            <div className="flex items-center gap-2 mb-3">
              {q.topic && <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>}
              {q.difficulty && <Badge className={q.difficulty === 'hard' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : q.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}>{q.difficulty}</Badge>}
            </div>
            <p className="text-base font-medium mb-4">{q.question}</p>
            <div className="space-y-2">
              {q.options?.map((opt, oi) => {
                const selected = answers[qIdx] === oi;
                return (
                  <button
                    key={oi}
                    onClick={() => setAnswers(a => ({ ...a, [qIdx]: oi }))}
                    data-testid={`dpp-opt-${oi}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${selected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-muted-foreground/40'}`}
                  >
                    <span className="text-xs font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setQIdx(Math.max(0, qIdx - 1))} disabled={qIdx === 0} data-testid="dpp-prev">Prev</Button>
            <div className="flex-1 flex flex-wrap gap-1 justify-center">
              {activeSubject.questions.map((_, i) => (
                <button key={i} onClick={() => setQIdx(i)} className={`w-7 h-7 rounded text-[10px] font-bold ${i === qIdx ? 'bg-indigo-500 text-white ring-2 ring-indigo-300' : answers[i] !== undefined ? 'bg-emerald-500 text-white' : 'bg-muted'}`} data-testid={`dpp-nav-${i}`}>
                  {i + 1}
                </button>
              ))}
            </div>
            {!isLast ? (
              <Button size="sm" onClick={() => setQIdx(qIdx + 1)} data-testid="dpp-next">Next</Button>
            ) : (
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={submit} disabled={submitting} data-testid="dpp-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Submit
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // RESULT VIEW
  if (result) {
    const pct = result.attempt?.percentage ?? 0;
    return (
      <div className="min-h-screen bg-background text-foreground" data-testid="dpp-result">
        <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
          <div className="text-center">
            <div className={`inline-flex w-20 h-20 rounded-full ${pct >= 60 ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-amber-100 dark:bg-amber-950/40'} items-center justify-center mb-3`}>
              {pct >= 60 ? <CheckCircle2 className="w-10 h-10 text-emerald-500" /> : <TrendingUp className="w-10 h-10 text-amber-500" />}
            </div>
            <h2 className="text-3xl font-bold" data-testid="dpp-score">{pct}%</h2>
            <p className="text-sm text-muted-foreground">{result.attempt.correct} / {result.attempt.total} correct · {result.attempt.subject}</p>
          </div>

          <div className="grid grid-cols-3 gap-3" data-testid="dpp-xp-streak">
            <div className="p-4 rounded-xl border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 text-center">
              <Zap className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">+{result.attempt.xp_earned}</p>
              <p className="text-[10px] text-muted-foreground">XP earned{result.streak.multiplier > 1 ? ` (${result.streak.multiplier}× streak)` : ''}</p>
            </div>
            <div className="p-4 rounded-xl border border-orange-300 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/30 text-center">
              <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{result.streak.current_streak}</p>
              <p className="text-[10px] text-muted-foreground">day streak</p>
            </div>
            <div className="p-4 rounded-xl border border-indigo-300 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 text-center">
              <Trophy className="w-5 h-5 text-indigo-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{result.streak.total_xp}</p>
              <p className="text-[10px] text-muted-foreground">total XP</p>
            </div>
          </div>

          {result.new_badges?.length > 0 && (
            <div className="p-5 rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20" data-testid="dpp-new-badges">
              <h3 className="font-bold text-center mb-3 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />New Badge{result.new_badges.length > 1 ? 's' : ''} Unlocked!
              </h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {result.new_badges.map(b => (
                  <Badge key={b} className="bg-amber-400 text-amber-900 text-sm py-1.5 px-3" data-testid={`new-badge-${b}`}>
                    <Award className="w-3.5 h-3.5 mr-1" />{b.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button className="flex-1" variant="outline" onClick={() => { setResult(null); setActiveSubject(null); }} data-testid="dpp-back-to-hub">
              Back to DPP Hub
            </Button>
            <Button className="flex-1" onClick={() => navigate('/error-book')} data-testid="dpp-goto-errorbook">
              <Target className="w-4 h-4 mr-1.5" />Review errors
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // HUB VIEW (default)
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="dpp-hub">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="dpp-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />Daily Practice
              </h1>
              <p className="text-xs text-muted-foreground">15 fresh questions per subject, every day. Keep your streak alive.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-sm font-semibold">Exam:</span>
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="dpp-exam-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {exams.map(e => <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Streak + XP + Countdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dpp-top-stats">
          <div className="p-5 rounded-2xl border border-orange-300 dark:border-orange-900 bg-gradient-to-br from-orange-50 to-rose-50 dark:from-orange-950/20 dark:to-rose-950/20" data-testid="dpp-streak-card">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="w-6 h-6 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground">Current Streak</p>
                <p className="text-3xl font-bold">{streak?.current_streak || 0} <span className="text-sm font-normal">days</span></p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Best: {streak?.longest_streak || 0} days ·
              {streak?.multiplier > 1 && <span className="ml-1 text-orange-600 font-semibold">{streak.multiplier}× XP multiplier</span>}
            </p>
          </div>

          <div className="p-5 rounded-2xl border border-amber-300 dark:border-amber-900 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20" data-testid="dpp-xp-card">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-6 h-6 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground">Total XP</p>
                <p className="text-3xl font-bold">{streak?.total_xp || 0}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{streak?.total_dpps || 0} DPPs solved · {streak?.perfect_count || 0} perfect</p>
          </div>

          {countdown?.has_date && (
            <div className={`p-5 rounded-2xl border ${countdown.days_remaining <= 30 ? 'border-rose-300 dark:border-rose-900 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20' : 'border-indigo-300 dark:border-indigo-900 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 dark:to-violet-950/20'}`} data-testid="dpp-countdown-card">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className={`w-6 h-6 ${countdown.days_remaining <= 30 ? 'text-rose-500' : 'text-indigo-500'}`} />
                <div>
                  <p className="text-xs text-muted-foreground">{countdown.label}</p>
                  <p className="text-3xl font-bold">{countdown.days_remaining} <span className="text-sm font-normal">days left</span></p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Exam date: {countdown.next_date}</p>
            </div>
          )}
        </div>

        {/* Today's subjects */}
        <div>
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-500" />Today's Practice — {today?.date}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dpp-subjects-grid">
            {today?.subjects?.map((s, i) => (
              <SubjectCard key={s.subject} subject={s} onStart={startSubject} index={i} />
            ))}
          </div>
        </div>

        {/* Badges */}
        {streak?.badge_labels?.length > 0 && (
          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="dpp-badges">
            <h2 className="font-bold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" />Your Badges</h2>
            <div className="flex flex-wrap gap-2">
              {streak.badge_labels.map(b => (
                <Badge key={b.id} className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 py-1 px-3" data-testid={`badge-${b.id}`}>
                  <Star className="w-3 h-3 mr-1" />{b.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* 30-day history grid */}
        {history?.grid?.length > 0 && (
          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="dpp-history">
            <h2 className="font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" />Last 30 Days</h2>
            <div className="grid grid-cols-10 sm:grid-cols-15 gap-1" style={{ gridTemplateColumns: 'repeat(15, minmax(0, 1fr))' }}>
              {history.grid.map((d, i) => (
                <div
                  key={i}
                  title={`${d.date}: ${d.subjects_done} subject${d.subjects_done !== 1 ? 's' : ''} · ${d.correct}/${d.total} · ${d.xp} XP`}
                  className={`h-8 rounded ${d.status === 'completed' ? 'bg-emerald-500' : d.status === 'partial' ? 'bg-amber-400' : 'bg-muted'}`}
                  data-testid={`history-day-${i}`}
                />
              ))}
            </div>
            <div className="flex gap-3 text-[10px] mt-2 text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />All subjects done</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" />Partial</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-muted" />Missed</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubjectCard({ subject, onStart, index }) {
  const done = subject.attempted;
  const ready = subject.ready !== false;
  const pct = done ? subject.attempt?.percentage : null;
  return (
    <div className={`p-5 rounded-2xl border-2 transition-all ${done ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20' : !ready ? 'border-border bg-card opacity-80' : 'border-border bg-card hover:border-indigo-400 hover:shadow-lg'}`} data-testid={`dpp-subject-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-xl ${done ? 'bg-emerald-500' : !ready ? 'bg-muted text-muted-foreground' : 'bg-indigo-500 text-white'} flex items-center justify-center`}>
            {done ? <CheckCircle2 className="w-5 h-5" /> : !ready ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 ml-0.5" />}
          </div>
          <div>
            <p className="font-bold text-sm">{subject.subject}</p>
            <p className="text-[10px] text-muted-foreground">{subject.count} questions</p>
          </div>
        </div>
        {done && <Badge className="bg-emerald-500 text-white">{pct}%</Badge>}
        {!done && !ready && <Badge variant="outline" className="text-[10px]">Generating…</Badge>}
      </div>
      {done ? (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{subject.attempt.correct}/{subject.attempt.total} correct · +{subject.attempt.xp_earned} XP</p>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : (
        <Button
          className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
          size="sm"
          onClick={() => onStart(subject)}
          data-testid={`dpp-start-${index}`}
        >
          {ready ? <><Play className="w-4 h-4 mr-1.5" />Start Practice</> : <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Tap to wait & start</>}
        </Button>
      )}
    </div>
  );
}
